import { trackEvent } from "lib/googleTagManager"

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

const days = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
]

/**
 * Make sure to always pass a **timezone**!
 *
 * JavaScript will do different conversions depending on the input:
 *
 * new Date("25 July 2016").getTime() !== new Date("2016-07-25").getTime()
 * new Date("2016-07-25").getTime() === new Date("2016-07-25T00:00:00Z").getTime()
 */
export const convertToCustomDateFormat = (date) => {
  if (date) {
    const d = new Date(date) // Converts to user local time or UTC time
    const year = d.getUTCFullYear()
    const day = d.getUTCDate()
    const monthName = months[d.getUTCMonth()]
    const dayName = days[d.getUTCDay()]

    return `${dayName}, ${monthName} ${day}, ${year}`
  }
  return null
}

/**
 * Returns date as "Wed, Sept. 22"
 *
 * @param {String} date
 */
export const convertToShortFormat = (date) => {
  if (date) {
    const d = new Date(date) // Converts to user local time or UTC time
    const day = d.getUTCDate()
    const monthName = months[d.getUTCMonth()]
    const dayName = days[d.getUTCDay()]

    return `${dayName.substring(0, 3)}., ${monthName.substring(0, 3)}. ${day}`
  }
  return null
}

/**
 * Returns day of the month.
 *
 * @param {String || Date} date
 */
const _getDay = (date) => {
  if (date) {
    const d = new Date(date)
    return d.getDate()
  }
  return null
}

/**
 * Returns day of the week. E.g.: Tuesday, Wednesday
 *
 * @param {String || Date} date
 */
export const getDayName = (date) => {
  if (date) {
    const d = new Date(date)
    return days[d.getDay()]
  }
  return null
}

/**
 * Returns month name. E.g.: October, November
 *
 * @param {String || Date} date
 */
const getMonthName = (date) => {
  if (date) {
    const d = new Date(date)
    return months[d.getMonth()]
  }
  return null
}

/**
 * Returns abbreviated month name. E.g.: OCT, NOV, DEC
 *
 * @param {String || Date} date
 */
export const getMonthAbbreviation = (date) =>
  getMonthName(date).substring(0, 3).toUpperCase()

/**
 * Returns date considering a timezone.
 *
 * @param {String} date - UTC format. E.g.: "2022-07-28T17:00:00Z"
 * @param {String} timezone - E.g.: "America/Los_angeles"
 */
export const getLocalizedDate = (date, timezone) => {
  const localizedDate = new Date(date).toLocaleString("en-US", {
    timeZone: timezone,
  })
  return new Date(localizedDate)
}

/**
 * Returns timezone name. E.g.: "PDT", "EST"
 *
 * @param {Date} date
 * @param {String} timezone - E.g.: "America/Los_angeles"
 */
export const getTimezoneName = (date, timezone) =>
  date
    .toLocaleTimeString("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
    })
    .split(" ")[2]

/**
 * Returns formatted time. E.g.: 2:00pm
 *
 * @param {Date} date
 */
export const getFormattedTime = (date) =>
  date
    .toLocaleString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    })
    .replace(" ", "")
    .toLowerCase()

/**
 * Resets time in date object.
 *
 * @param {Date} date
 */
export const resetTime = (date) => {
  date.setHours(0)
  date.setMinutes(0)
  date.setSeconds(0)
  return date
}

/**
 * Converts date object to date in PDT timezone.
 *
 * @param {Date} date
 */
export const convertToPDT = (date) => {
  const strDate = date.toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
  })
  return new Date(strDate)
}

/**
 * Returns formatted price. E.g.: $1,430
 *
 * @param {Number} value
 */
function _toPrice(value) {
  return `$${Intl.NumberFormat().format(value)}`
}

const _handleManagerEvent = (event) => {
  const { message, value } = event.data
  if (message === "trackEvent") {
    // value: Begin Registration-(starter|professional)|Complete Registration-(starter|professional)
    if (value.startsWith("Begin")) {
      trackEvent("buttonClick", { buttonClickEventName: value })
    } else if (value.startsWith("Complete")) {
      trackEvent("formSubmission", {
        formSubmissionEventName: value,
      })
    }
  }
  if (message === "redirect") {
    window.location.assign(value)
  }
}

export const getPageUrl = (typename, slug) => {
  // Handle blog posts (both Contentful and local)
  if (
    typename === "ContentfulBlog" ||
    typename === "MarkdownRemark" ||
    typename === "LocalBlog"
  ) {
    return `/blog/${slug}/`
  }
  // Handle events (both Contentful and local)
  if (
    typename === "ContentfulEvent" ||
    typename === "EventsYaml" ||
    typename === "LocalEvent"
  ) {
    return `/events/${slug}/`
  }
  return null
}

export function hrefIsRelative(href) {
  return href.startsWith("/")
}
